<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { LANDING_PAGE } from '@/types'
import { useDashboardToast } from '@/composables/useDashboardToast'
import { useRenewAuthorisationModal } from '@/composables/useRenewAuthorisationModal'
import DashboardNavIcon from '@/components/dashboard/shell/DashboardNavIcon.vue'
import PersonalDetailsEditForm from '@/components/views/manage-account/PersonalDetailsEditForm.vue'
import RenewAuthorisationDeclarationForm from '@/components/dashboard/authorisation/RenewAuthorisationDeclarationForm.vue'
import PersonalDetailsLetterhead from '@/components/dashboard/shared/PersonalDetailsLetterhead.vue'
import { usePersonalDetailsDisplay } from '@/composables/usePersonalDetailsDisplay'
import DashboardModal from '@/components/dashboard/ui/DashboardModal.vue'

type RenewPanelView = 'authorise' | 'details'

const { t } = useI18n()
const store = useStore()
const { showToast } = useDashboardToast()
const { isOpen, closeRenewAuthorisationModal } = useRenewAuthorisationModal()

const panelView = ref<RenewPanelView>('authorise')
const declarationFormRef = ref<InstanceType<typeof RenewAuthorisationDeclarationForm> | null>(
  null
)
const termsAgreed = ref(false)
const isSubmitting = ref(false)

const { details } = usePersonalDetailsDisplay()

watch(isOpen, (open) => {
  if (!open) {
    panelView.value = 'authorise'
    termsAgreed.value = false
    declarationFormRef.value?.reset()
  }
})

const showDetailsForm = () => {
  panelView.value = 'details'
}

const showAuthoriseView = () => {
  panelView.value = 'authorise'
}

const onDetailsSaved = () => {
  showAuthoriseView()
}

const onSuccess = () => {
  store.commit('SET_LANDING_PAGE', LANDING_PAGE.DASHBOARD)
  closeRenewAuthorisationModal()
  showToast(t('Dashboard.Status.RenewModal.ActivatedToast'))
}

const submitRenewal = async () => {
  if (!termsAgreed.value || !declarationFormRef.value) {
    return
  }

  isSubmitting.value = true
  try {
    await declarationFormRef.value.submitAuthorisation()
  } catch (error) {
    console.error('Failed to renew Swiftaid authorisation', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <DashboardModal
    :is-open="isOpen"
    modal-class="sa-renew-auth-modal"
    :aria-labelledby="panelView === 'details' ? 'renew-details-heading' : 'renew-auth-title'"
    :close-disabled="isSubmitting"
    @close="closeRenewAuthorisationModal"
  >
      <template v-if="panelView === 'details'" #header-start>
        <button type="button" class="sa-modal-back" @click="showAuthoriseView">
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fill-rule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('Dashboard.Status.RenewModal.BackToAuthorisation') }}
        </button>
      </template>

      <template v-if="panelView === 'authorise'">
        <div class="sa-modal-icon sa-renew-auth-modal__icon" aria-hidden="true">
          <DashboardNavIcon name="matching" />
        </div>

        <h2 id="renew-auth-title" class="sa-renew-auth-modal__title">
          {{ $t('Dashboard.Status.RenewModal.Title') }}
        </h2>

        <p class="sa-renew-auth-modal__intro">
          {{ $t('Dashboard.Status.RenewModal.Intro') }}
        </p>

        <PersonalDetailsLetterhead
          v-if="details"
          variant="card"
          show-edit
          class="sa-renew-auth-modal__details"
          :name-label="$t('Dashboard.Settings.Personal.FullName')"
          :address-label="$t('Dashboard.Settings.Personal.Address')"
          :edit-label="$t('Dashboard.Status.RenewModal.EditDetails')"
          @edit="showDetailsForm"
        />

        <RenewAuthorisationDeclarationForm
          ref="declarationFormRef"
          v-model:terms-agreed="termsAgreed"
          compact
          terms-input-id="renew-auth-terms"
          :show-submit="false"
          @success="onSuccess"
        />

      </template>

      <template v-else>
        <h2 id="renew-details-heading" class="sa-renew-auth-modal__title">
          {{ $t('Dashboard.Status.RenewModal.EditDetailsHeading') }}
        </h2>

        <p class="sa-renew-auth-modal__intro">
          {{ $t('Dashboard.Status.RenewModal.EditDetailsIntro') }}
        </p>

        <div class="sa-renew-auth-modal__form">
          <PersonalDetailsEditForm
            @saved="onDetailsSaved"
            @cancel-edit="showAuthoriseView"
          />
        </div>
      </template>

    <template v-if="panelView === 'authorise'" #footer>
      <div class="sa-modal-actions sa-renew-auth-modal__actions">
        <button
          type="button"
          class="sa-btn-secondary"
          :disabled="isSubmitting"
          @click="closeRenewAuthorisationModal"
        >
          {{ $t('Buttons.Cancel') }}
        </button>
        <button
          type="button"
          class="sa-btn-primary"
          :disabled="!termsAgreed || isSubmitting"
          :aria-busy="isSubmitting"
          @click="submitRenewal"
        >
          <span v-if="isSubmitting" class="spinner sa-btn-spinner" aria-hidden="true" />
          <span>{{ $t('Dashboard.Status.RenewModal.Submit') }}</span>
        </button>
      </div>
    </template>
  </DashboardModal>
</template>
